import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useCombat } from './hooks/useCombat';
import { useCombatStore } from './store/combatStore';
import { authApi } from './auth/authApi';
import { CombatGrid } from './components/CombatGrid/CombatGrid';
import { CombatHUD } from './components/CombatHUD/CombatHUD';
import { CombatLog } from './components/CombatLog/CombatLog';
import { useTranslation } from './i18n/useTranslation';
import { useAuthStore } from './auth/authStore';
import { useTurnTimer } from './hooks/useTurnTimer';
import { ToastContainer, useToast } from './components/Toast/Toast';
import { loadoutKeyFor } from './systems/loadout';
import type { Spell } from './types/interfaces';
import { missionApi } from './api/missionApi';
import { missionView } from './components/Mission/missionView';
import MissionPortalBanner from './components/Mission/MissionPortalBanner';
import ResultCard from './components/Result/ResultCard';
import { announcerArtUrl, loadAnnouncers, type AnnouncerBundle } from './api/announcerApi';
import type { AnnouncerData } from './api/adminApi';
import { pickAnnouncerLine } from './utils/announcerPick';
import { combatHasBirds } from './utils/ambientBirds';
import { effectiveWeather, fogHiddenIds, weatherNotice } from './utils/weatherRules';

/** Duração da entrada no portal (escuridão fechando até a tela ficar preta). */
const PORTAL_WARP_MS = 1300;
import './App.css';

export default function App() {
  // Read params inside the component so React Router's current URL is used,
  // not the URL at module-load time (which would be '/' or '/user').
  const [searchParams] = useSearchParams();
  const combatId = searchParams.get('combat_id') ?? 'demo';
  const entityId = searchParams.get('entity_id') ?? 'player_1';

  const navigate  = useNavigate();
  const { user, token }  = useAuthStore();
  const { t, language, setLanguage } = useTranslation();
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  /** Locutor + fala do quadro de fim — sorteados uma vez por combate. */
  const quoteRef = useRef<{ key: string; text: string; announcer: AnnouncerData | null } | null>(null);
  const [announcerBundle, setAnnouncerBundle] = useState<AnnouncerBundle | null>(null);
  useEffect(() => { loadAnnouncers().then(setAnnouncerBundle).catch(() => { /* usa as frases embutidas */ }); }, []);
  const { messages, toast, remove } = useToast();

  // Muda a cada COMBAT_STATE — a CommandBar reusa isto para reler as
  // quantidades de item (um consumível pode ter sido gasto nesta jogada).
  const [combatNonce, setCombatNonce] = useState(0);

  const {
    combat,
    myEntity,
    myTurn,
    selectedSpell,
    highlights,
    cellHover,
    connected,
    abandon,
    selectSpell,
    endTurn,
    useItem,
    onCellHover,
    onCellClick,
    walkTo,
  } = useCombat({ combatId, myEntityId: entityId, toast });

  useEffect(() => { setCombatNonce(n => n + 1); }, [combat]);

  /**
   * Navigate back to the correct home depending on the user's role.
   * If the combat is still active (not finished or already abandoned), stamp
   * it as ABANDONED and close the WebSocket before navigating so the server
   * connection is freed immediately rather than at component unmount.
   */
  const handleExitClick = () => {
    const phase = combat?.phase;
    if (phase !== 'FINISHED' && phase !== 'ABANDONED') {
      setShowExitConfirm(true);
    } else {
      doExit();
    }
  };

  const doExit = () => {
    setShowExitConfirm(false);
    const phase = combat?.phase;
    if (phase !== 'FINISHED' && phase !== 'ABANDONED') {
      if (combat?.id) {
        sessionStorage.setItem('abandoned_combat', combat.id);
      }
      abandon();
    }
    navigate(user?.role === 'admin' ? '/admin' : '/user', { replace: true });
  };

  // ── "Nova Batalha" (overlay de fim de combate, só jogador) ────────────────
  // Com energia → começa outra batalha na hora. Sem energia (ou erro) → volta
  // ao início levando a mensagem do servidor para o dashboard exibir.
  const [startingNew, setStartingNew] = useState(false);
  const handleNewBattle = async () => {
    if (startingNew || !token) return;
    setStartingNew(true);
    try {
      const res = await authApi.startBattle(token, false);
      useCombatStore.getState().resetCombat();
      navigate(`/play?combat_id=${res.combat_id}&entity_id=${res.player_entity_id}`);
    } catch (e) {
      sessionStorage.setItem('battle_start_error', (e as Error).message);
      navigate('/user', { replace: true });
    } finally {
      setStartingNew(false);
    }
  };

  // ── Missão ────────────────────────────────────────────────────────────────
  // Nível vencido → o servidor abre um portal (combat.mission.portal) onde caiu
  // um monstro; a célula é injetada na grade como portal de missão (visual
  // próprio + animação de surgimento). O jogador anda até ele (sem turno) e,
  // ao lado do portal, entra: cria o próximo nível sem sair de /play.
  const mView = combat && combat.id === combatId && myEntity ? missionView(combat, myEntity.id) : null;
  const portal = mView === 'portal' ? combat?.mission?.portal ?? null : null;
  const gridMap = useMemo(() => {
    if (!combat || !portal) return combat?.map;
    const cells = combat.map.cells.map(row => row.map(c =>
      c.x === portal.x && c.y === portal.y
        ? { ...c, type: 'battle_point' as any, portal_label: t('mission.result.portalSign'), portal_style: 'mission' as const, portal_spawn: true }
        : c));
    return { ...combat.map, cells };
  }, [combat, portal, t]);

  // ── Clima que afeta o jogo (missões) ──────────────────────────────────────
  // Neblina: inimigos longe do meu time não são desenhados (o servidor também
  // recusa mirar neles). Aviso fixo explica o efeito do clima do nível.
  const weather = combat ? effectiveWeather(combat) : 'NONE';
  const myTeam = myEntity?.team ?? 'player';
  const visibleEntities = useMemo(() => {
    if (!combat) return {};
    const hidden = fogHiddenIds(combat.entities, myTeam, weather);
    if (hidden.size === 0) return combat.entities;
    return Object.fromEntries(Object.entries(combat.entities).filter(([id]) => !hidden.has(id)));
  }, [combat, myTeam, weather]);
  const weatherHint = weatherNotice(weather);

  // ── Entrada no portal ─────────────────────────────────────────────────────
  // O personagem pisa no portal e a escuridão se fecha sobre ele (CombatGrid,
  // `portalWarp`); com a tela preta troca de combate, e o próximo mapa surge
  // clareando do preto (sessionStorage 'mission_fade_in').
  // A troca acontece UMA vez só: quem entrou e o acompanhamento da equipe
  // (poll abaixo) disputavam a navegação — o segundo limpava o estado do mapa
  // novo já carregado (tela "carregando" e indicador "desconectado").
  const [enteringPortal, setEnteringPortal] = useState(false);
  const [warpT0, setWarpT0] = useState<number | null>(null);
  const [portalError, setPortalError] = useState<string | null>(null);
  const shownCombatRef = useRef(combatId);
  shownCombatRef.current = combatId;
  const switchingRef = useRef(false);

  const warpTo = async (next: Promise<{ combat_id: string; entity_id: string }>) => {
    if (switchingRef.current) return;
    switchingRef.current = true;
    const from = shownCombatRef.current;
    setEnteringPortal(true);
    setWarpT0(performance.now());
    setPortalError(null);
    try {
      const [dest] = await Promise.all([next, new Promise(r => setTimeout(r, PORTAL_WARP_MS))]);
      if (shownCombatRef.current !== from) return; // já trocou por outro caminho
      sessionStorage.setItem('mission_fade_in', '1');
      useCombatStore.getState().resetCombat();
      navigate(`/play?combat_id=${dest.combat_id}&entity_id=${dest.entity_id}`, { replace: true });
    } catch (e) {
      setPortalError((e as Error).message);
    } finally {
      setEnteringPortal(false);
      setWarpT0(null);
      switchingRef.current = false;
    }
  };

  const handleEnterPortal = () => {
    const runId = combat?.mission?.run_id;
    if (switchingRef.current || !token || !runId) return;
    // Pisa dentro do portal (MOVE pós-vitória) enquanto a escuridão se fecha.
    if (portal && myEntity && myEntity.hp > 0 && (myEntity.position_x !== portal.x || myEntity.position_y !== portal.y)) {
      walkTo(portal.x, portal.y);
    }
    void warpTo(missionApi.advance(token, runId).then(r => ({ combat_id: r.combat_id, entity_id: r.player_entity_id })));
  };

  // Mapa novo depois do portal: surge clareando do preto.
  const [fadeIn, setFadeIn] = useState(false);
  useEffect(() => {
    if (combat && combat.id === combatId && sessionStorage.getItem('mission_fade_in')) {
      sessionStorage.removeItem('mission_fade_in');
      setFadeIn(true);
    }
  }, [combat?.id, combatId]); // eslint-disable-line react-hooks/exhaustive-deps

  // O jogador precisa ir até o portal: no portal ou ao lado (sem diagonal) entra.
  const nearPortal = !!(portal && myEntity &&
    Math.abs(myEntity.position_x - portal.x) + Math.abs(myEntity.position_y - portal.y) <= 1);
  const [walkingToPortal, setWalkingToPortal] = useState(false);
  const walkDelayMs = useRef(0);
  /** Clique no portal / botão do banner: anda até o lado do portal e entra. */
  const goToPortal = () => {
    if (!portal || !combat || !myEntity || myEntity.hp <= 0 || enteringPortal) return;
    if (nearPortal) { handleEnterPortal(); return; }
    const me = myEntity;
    const occupied = new Set(Object.values(combat.entities).filter(e => e.hp > 0).map(e => `${e.position_x},${e.position_y}`));
    const spots = [[0, 1], [1, 0], [0, -1], [-1, 0]]
      .map(([dx, dy]) => ({ x: portal.x + dx, y: portal.y + dy }))
      .filter(c => combat.map.cells[c.y]?.[c.x]?.type === 'Walkable' && !occupied.has(`${c.x},${c.y}`))
      .sort((a, b) => (Math.abs(a.x - me.position_x) + Math.abs(a.y - me.position_y)) - (Math.abs(b.x - me.position_x) + Math.abs(b.y - me.position_y)));
    if (spots.length === 0) return;
    const dist = Math.abs(spots[0].x - me.position_x) + Math.abs(spots[0].y - me.position_y);
    walkDelayMs.current = dist * 190 + 250; // espera o passo animado terminar
    setWalkingToPortal(true);
    walkTo(spots[0].x, spots[0].y);
  };
  // Chegou ao lado do portal andando (servidor confirmou) → entra.
  useEffect(() => {
    if (!walkingToPortal || !nearPortal) return;
    const id = setTimeout(() => { setWalkingToPortal(false); handleEnterPortal(); }, walkDelayMs.current);
    return () => clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walkingToPortal, nearPortal]);

  // Em equipe: outro membro pode entrar no portal primeiro. Enquanto o portal
  // está aberto, consulta a tentativa; surgiu combate novo → vai junto.
  const missionRunId = mView === 'portal' ? combat?.mission?.run_id ?? null : null;
  useEffect(() => {
    if (!missionRunId || !token) return;
    const id = setInterval(async () => {
      if (switchingRef.current) return; // eu mesmo já estou entrando
      try {
        const info = await missionApi.getRun(token, missionRunId);
        const next = info.run.combat_id;
        if (switchingRef.current || info.run.status !== 'active' || !next || next === shownCombatRef.current) return;
        clearInterval(id);
        void warpTo(Promise.resolve({ combat_id: next, entity_id: info.run.my_entity_id })); // mesma entrada de quem foi primeiro
      } catch { /* tenta de novo no próximo tick */ }
    }, 3000);
    return () => clearInterval(id);
  }, [missionRunId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Fim da missão: concluída → Hub (onde fica o portal); derrota → Hub ou painel (config da missão). */
  const handleMissionExit = async () => {
    const runId = combat?.mission?.run_id;
    if (mView === 'completed') {
      sessionStorage.setItem('user_view_override', 'map');
    } else if (token && runId) {
      try {
        const info = await missionApi.getRun(token, runId);
        sessionStorage.setItem('user_view_override', info.mission?.defeat_destination === 'dashboard' ? 'layout' : 'map');
      } catch { /* sem info → abre a vista padrão */ }
    }
    navigate('/user', { replace: true });
  };

  const clearHover    = useCombatStore((s) => s.clearHover);
  const serverError   = useCombatStore((s) => s.serverError);
  const lastMovements = useCombatStore((s) => s.lastMovements);

  // Turn countdown: seconds left for the active player to act (null = AI turn)
  const turnSecondsLeft = useTurnTimer(combat, myTurn);

  // ── Loading / Error ───────────────────────────────────────────────────────
  // Guard: also reject stale combat from a previous session (combat.id doesn't
  // match the URL's combat_id yet). Without this, the old FINISHED combat would
  // briefly render the result overlay before resetCombat() fires in ws.onopen.
  if (!combat || combat.id !== combatId) {
    // Trocando de mapa pelo portal: mantém a tela preta até o mapa novo chegar.
    if (sessionStorage.getItem('mission_fade_in')) return <div className="portalBlack" />;
    return (
      <div className="loading">
        <div className="loadingDot" />
        {serverError ? (
          <span style={{ color: '#ff6b6b', fontSize: 14, maxWidth: 400, textAlign: 'center' }}>
            ⚠ {serverError}
          </span>
        ) : (
          <span>{connected ? t('app.loadingCombat') : t('app.connecting')}</span>
        )}
      </div>
    );
  }

  // ── Player spells: embedded in server state ───────────────────────────────
  const playerSpells: Spell[] = (myEntity?.spells ?? []) as Spell[];

  // ── Combat phase banner ───────────────────────────────────────────────────
  const phaseMsg =
    combat.phase === 'FINISHED'
      ? t('app.combatFinished')
      : combat.phase === 'POSITIONING'
      ? t('app.positioning')
      : null;

  const isFinished = combat.phase === 'FINISHED';
  const isVictory  = isFinished && (myEntity ? myEntity.hp > 0 : false);
  const isAdmin    = user?.role === 'admin';

  // ── Quadro de fim: quem fala e o que diz ──────────────────────────────────
  // Missão: vale o resultado do TIME (em equipe, quem caiu também "venceu").
  // Fala um LOCUTOR sorteado (reino na vitória, monstro na derrota — admin).
  // Sem locutor (API fora): busto do meu personagem / do monstro mais inteiro.
  const resultWin = mView ? mView === 'completed' : isVictory;
  const foe = Object.values(combat.entities)
    .filter(e => e.team !== (myEntity?.team ?? 'player'))
    .sort((a, b) => b.hp - a.hp)[0];
  const quoteKey = `${combat.id}:${resultWin ? 'victory' : 'defeat'}`;
  if (isFinished && quoteRef.current?.key !== quoteKey) {
    const pick = pickAnnouncerLine(announcerBundle, resultWin ? 'victory' : 'defeat', language,
      { jogador: myEntity?.name, monstro: foe?.name });
    quoteRef.current = { key: quoteKey, text: pick.text, announcer: pick.announcer };
  }
  const resultQuote = quoteRef.current?.text ?? '';
  const announcer = quoteRef.current?.announcer ?? null;
  const speakerEntity = resultWin ? myEntity : foe;
  const resultSpeaker = announcer
    ? { image: announcerArtUrl(announcer.image), name: announcer.name }
    : speakerEntity
      ? { sprite: speakerEntity.sprite, cfg: speakerEntity.sprite_config ?? null, name: speakerEntity.name }
      : null;

  return (
    <div className="app">
      <ToastContainer messages={messages} onRemove={remove} />
      {/* ── Top bar ── */}
      <div className="topBar">
        {/* Connection status */}
        <div className={`wsStatus ${connected ? 'connected' : 'disconnected'}`}>
          {connected ? t('app.online') : t('app.offline')}
        </div>

        {/* Language toggle */}
        <div style={{ display: 'flex', gap: 4 }}>
          {(['en', 'pt'] as const).map((lang) => (
            <button
              key={lang}
              onClick={() => setLanguage(lang)}
              className={`langBtn${language === lang ? ' langBtnActive' : ''}`}
            >
              {lang.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Exit button — always visible */}
        <button className="exitBtn" onClick={handleExitClick} title={t('hud.exitConfirmTitle')}>
          {isAdmin ? t('app.adminPanel') : t('app.exit')}
        </button>
      </div>

      {/* Phase banner (positioning only — finished uses the overlay) */}
      {phaseMsg && !isFinished && <div className="phaseBanner">{phaseMsg}</div>}
      {weatherHint && !isFinished && <div className="weatherHint" role="note">{t(weatherHint)}</div>}
      {/* Portal: a tela termina de escurecer (a grade já fecha a escuridão sobre o portal) */}
      {enteringPortal && <div className="portalBlackout" aria-hidden="true" />}
      {fadeIn && <div className="portalFadeIn" aria-hidden="true" onAnimationEnd={() => setFadeIn(false)} />}

      {/* Turn countdown moved to CombatGrid */}

      {/* ── Main layout ── */}
      <div className="gameLayout">
        <CombatGrid
          map={gridMap ?? combat.map}
          entities={visibleEntities}
          movements={lastMovements}
          myEntityId={myEntity?.id ?? null}
          highlights={highlights}
          cellHover={cellHover}
          onCellClick={(x, y) => {
            if (mView === 'portal') {
              // Pós-vitória: andar livre; clicar no portal anda até ele e entra.
              if (portal && x === portal.x && y === portal.y) { goToPortal(); return; }
              if (myEntity && myEntity.hp > 0) { setWalkingToPortal(false); walkTo(x, y); }
              return;
            }
            onCellClick(x, y);
          }}
          onCellHover={onCellHover}
          onMouseLeave={clearHover}
          turnSecondsLeft={!isFinished && myTurn ? turnSecondsLeft : null}
          backgroundTheme={combat.map.background_theme}
          weatherType={combat.map.weather_type}
          birdSpawner={combatHasBirds(combat.id, combat.map.weather_type) ? 'hub_birds' : null}
          portalWarp={warpT0 !== null && portal ? { x: portal.x, y: portal.y, t0: warpT0, durationMs: PORTAL_WARP_MS } : null}
          stormMarks={weather === 'STORM' ? combat.weather?.storm_marks : undefined}
          stormStrikes={weather === 'STORM' ? combat.weather?.storm_strikes : undefined}
          backgroundEnabled={combat.map.background_enabled}
        />
        <CombatLog entries={combat.log} />
      </div>

      {/* HUD — player only */}
      {myEntity && (
        <CombatHUD
          entity={myEntity}
          spells={playerSpells}
          selectedSpellId={selectedSpell?.id ?? null}
          myTurn={myTurn}
          token={token ?? ''}
          loadoutKey={loadoutKeyFor(myEntity.class ?? myEntity.sprite)}
          refreshNonce={combatNonce}
          onSelectSpell={(s) => selectSpell(s)}
          onUseItem={useItem}
          onEndTurn={endTurn}
          onExit={handleExitClick}
          toast={toast}
        />
      )}

      {/* ── Missão: portal aberto (o mapa continua visível) ── */}
      {mView === 'portal' && combat.mission && (
        <MissionPortalBanner
          level={combat.mission.level}
          entering={enteringPortal}
          error={portalError}
          near={nearPortal}
          canWalk={!!myEntity && myEntity.hp > 0}
          walking={walkingToPortal}
          onEnter={goToPortal}
        />
      )}

      {/* ── Combat-end overlay ── */}
      {isFinished && mView !== 'portal' && (
        <ResultCard
          kind={resultWin ? 'victory' : 'defeat'}
          title={resultWin ? t('result.victory') : t('result.defeat')}
          message={mView === 'completed' ? t('mission.result.completedMsg')
            : mView === 'failed' ? t('mission.result.failedMsg').replace('{n}', String((combat.mission?.level ?? 0) + 1))
            : isVictory ? t('result.victoryMsg') : t('result.defeatMsg')}
          speaker={resultSpeaker}
          quote={resultQuote}
        >

            {/* Recompensas / Perdas */}
            {myEntity && combat.rewards && combat.rewards[myEntity.id] && (
              <div style={{
                background: 'rgba(0, 0, 0, 0.4)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 8,
                padding: '12px 16px',
                marginTop: 16,
                marginBottom: 20,
                textAlign: 'left',
                fontSize: 14,
                color: '#ddd'
              }}>
                <h4 style={{ margin: '0 0 8px 0', color: isVictory ? '#44ff44' : '#ff4444', textTransform: 'uppercase', fontSize: 12 }}>
                  {isVictory ? t('result.spoils') : t('result.losses')}
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {combat.rewards[myEntity.id].xp > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 16 }}>✨</span>
                      <span>{t('result.xpGained')} <strong>{combat.rewards[myEntity.id].xp} {t('result.xp')}</strong></span>
                      {combat.rewards[myEntity.id].levelUp && (
                        <span style={{ color: '#ffd700', fontWeight: 'bold', fontSize: 12, marginLeft: 4, background: 'rgba(255,215,0,0.15)', padding: '2px 6px', borderRadius: 4 }}>
                          {t('result.levelUp')}
                        </span>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 16 }}>⭐</span>
                    <span>
                      {combat.rewards[myEntity.id].points >= 0 ? t('result.rankingGained') : t('result.rankingLost')}
                      {' '}
                      <strong style={{ color: combat.rewards[myEntity.id].points >= 0 ? '#44ff44' : '#ff4444' }}>
                        {Math.abs(combat.rewards[myEntity.id].points)} {t('result.rankingPoints')}
                      </strong>
                    </span>
                  </div>

                  {combat.rewards[myEntity.id].items && combat.rewards[myEntity.id].items.length > 0 && (() => {
                    // Agrupar itens repetidos: { "Fluxos": 2, "Espada": 1 }
                    const grouped = combat.rewards[myEntity.id].items.reduce<Record<string, number>>((acc, item) => {
                      acc[item] = (acc[item] ?? 0) + 1;
                      return acc;
                    }, {});
                    return (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <span style={{ fontSize: 16 }}>🎁</span>
                        <div>
                          <span style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#a09060' }}>
                            {t('result.itemsDropped')}:
                          </span>
                          <ul style={{ margin: '4px 0 0 16px', padding: 0, color: '#c8b870', listStyle: 'disc' }}>
                            {Object.entries(grouped).map(([name, qty]) => (
                              <li key={name}>
                                {qty > 1 && <strong style={{ color: '#ffd700' }}>{qty}× </strong>}
                                <strong>{name}</strong>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            <div className="resultActions">
              {/* Users get a shortcut to start a new battle immediately */}
              {mView && (
                <button className="resultBtnPrimary" onClick={() => void handleMissionExit()}>
                  {t('mission.result.back')}
                </button>
              )}
              {!isAdmin && !mView && (
                <button
                  className="resultBtnPrimary"
                  onClick={handleNewBattle}
                  disabled={startingNew}
                >
                  {startingNew ? t('app.connecting') : t('result.newBattle')}
                </button>
              )}

              <button
                className="resultBtnSecondary"
                onClick={doExit}
              >
                {isAdmin ? t('app.backAdmin') : t('app.backUser')}
              </button>
            </div>
        </ResultCard>
      )}
      {/* ── Confirm Exit Modal (App level) ── */}
      {showExitConfirm && (
        <div className="modalOverlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' }}>
          <div className="modalContent" style={{ background: 'rgba(15,20,15,0.95)', border: '1px solid #3a5a3a', borderRadius: 8, padding: 24, minWidth: 320, boxShadow: '0 10px 40px rgba(0,0,0,0.8)' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 18, color: '#e8c87a', textAlign: 'center', textTransform: 'uppercase', borderBottom: '1px solid rgba(232,200,122,0.2)', paddingBottom: 12 }}>
              {t('hud.exitConfirmTitle')}
            </h3>
            <p style={{ color: '#c8e6c8', fontSize: 14, marginBottom: 24, textAlign: 'center' }}>
              {t('hud.exitConfirmText')}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button 
                onClick={() => setShowExitConfirm(false)}
                style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#ccc', borderRadius: 4, cursor: 'pointer' }}
              >
                {t('common.cancel')}
              </button>
              <button 
                onClick={doExit}
                style={{ padding: '8px 16px', background: 'linear-gradient(135deg, #cc4444, #ff6666)', border: 'none', color: '#fff', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
              >
                {t('hud.exitConfirmBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
